import { toAuthorSlug } from './author-slug'
import { extractId, extractName } from '#shared/utils/solr-facet'

export interface AuthorCandidateSource {
  author?: string[]
  author_list?: string[]
}

export interface AuthorCandidate {
  label: string
  ezObjectId?: string
  fallbackSlug?: string
}

function parseAuthorEntry(raw: string): AuthorCandidate | null {
  const label = extractName(raw).trim()
  if (!label) {
    return null
  }
  const idPart = raw.includes('|') ? extractId(raw).trim() : ''
  const ezObjectId = /^\d+$/.test(idPart) ? idPart : undefined
  const fallbackSlug = ezObjectId ? undefined : (idPart || toAuthorSlug(label))
  return { label, ezObjectId, fallbackSlug }
}

function dedupeByIdentity(candidates: AuthorCandidate[]): AuthorCandidate[] {
  const seen = new Set<string>()
  const result: AuthorCandidate[] = []
  for (const c of candidates) {
    const key = c.ezObjectId ?? c.fallbackSlug ?? c.label.toLowerCase()
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    result.push(c)
  }
  return result
}

/**
 * Build authors from `author_list` with fallback to `author`.
 * Deduplicate by identity and ensure missing co-authors are not dropped.
 */
export function createAuthorCandidates(input: AuthorCandidateSource): AuthorCandidate[] {
  const listCandidates = dedupeByIdentity(
    (input.author_list ?? []).flatMap(raw => parseAuthorEntry(raw) ?? []),
  )

  const authorNames = input.author ?? []

  if (listCandidates.length === 0) {
    return dedupeByIdentity(authorNames.flatMap(raw => parseAuthorEntry(raw) ?? []))
  }

  if (authorNames.length <= listCandidates.length) {
    return listCandidates
  }

  const byName = new Map<string, AuthorCandidate[]>()
  for (const c of listCandidates) {
    const key = c.label.toLowerCase()
    const bucket = byName.get(key)
    if (bucket) {
      bucket.push(c)
    }
    else {
      byName.set(key, [c])
    }
  }

  const consumed = new Set<AuthorCandidate>()
  const merged: AuthorCandidate[] = []
  for (const raw of authorNames) {
    const label = extractName(raw).trim()
    if (!label) {
      continue
    }
    const match = byName.get(label.toLowerCase())?.find(c => !consumed.has(c))
    if (match) {
      consumed.add(match)
      merged.push(match)
    }
    else {
      // No match in `author_list`, render as plain text (no id/slug)
      merged.push({ label })
    }
  }

  // Keep any author_list entries whose name never appeared in `author`.
  for (const c of listCandidates) {
    if (!consumed.has(c)) {
      merged.push(c)
    }
  }

  return dedupeByIdentity(merged)
}
